// dependencies
import { useApolloClient } from "@apollo/client";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

import BeforeUnload from "../../../../components/BeforeUnload";
import PageTitle from "../../../../components/PageTitle";
import Pagination from "../../../../components/Pagination";
import Spacer from "../../../../components/Spacer";
import Spinner from "../../../../components/Spinner/ComponentLoader";

import type { SelectedFilter } from "../../schemas/report";

// hooks
import useReportStore from "./store";
import { ChartType } from "./store/enums";

import { useCompanyBasic, useRbacPrimaryRolePermissions } from "../../../../api/hooks";
import { useNavigationUtils } from "../../../../hooks/useNavigationUtils";

// selectors
import { getCompanyDecimalPlacesSetting } from "../../../../selectors/settings";

import {
	clampPageSize,
	getReportTypeSpecificPermissions,
	getUserSpecificCompanySettingDependentPermissions,
} from "./utils";

import ErrorPage from "../../../Error/ErrorPage";
import { defaultReportPageSize, pageSizes } from "../../config";
// components
import Filters from "./Components/Filters";
import GroupQuestionModal from "./Components/QuestionGroupingModal";
import ReportHeader from "./Components/ReportHeader";
import ReportTable from "./Components/ReportTable";
// styles
import css from "./Report.module.scss";

const AnalyticsReport = () => {
	const isInitialized = useRef(false);
	const { mergeSearchParameters } = useNavigationUtils();
	const [searchParams] = useSearchParams();

	const { reportID } = useParams<{ reportID: string }>();
	const shouldOpenColumnPicker = searchParams.get("newReport") === "true";

	const { company } = useCompanyBasic();

	const { data: permissions } = useRbacPrimaryRolePermissions("report_analytics");

	const { managerPermissions, contributorPermissions, groupAdminPermissions } =
		getUserSpecificCompanySettingDependentPermissions();

	const { permissionToCreateReviewReport } = getReportTypeSpecificPermissions(permissions);

	const hasManagerAccess =
		managerPermissions.canViewGoalReports ||
		managerPermissions.canViewReviewReports ||
		managerPermissions.canViewEmployeeReports ||
		managerPermissions.canViewSurveyReports;
	const hasContributorAccess =
		contributorPermissions.canViewGoalReports ||
		contributorPermissions.canViewReviewReports ||
		contributorPermissions.canViewEmployeeReports ||
		contributorPermissions.canViewSurveyReports;
	const hasGroupAdminAccess = Object.values(groupAdminPermissions)?.some(Boolean);

	const permissionToViewSharedWithReports = permissions?.length
		? hasManagerAccess || hasContributorAccess || hasGroupAdminAccess
		: undefined;

	const viewReportReportPermissionDenied =
		permissions?.length && !permissionToCreateReviewReport && !permissionToViewSharedWithReports;

	// store
	const [
		report,
		saveReport,
		saveAsReport,
		setClient,
		activeModal,
		selectedQuickFilter,
		setSelectedQuickFilter,
		applySelectedQuickFilter,
		getFilterOptions,
		filterOptions,
		getHasChanges,
		pagination,
		setPagination,
		gridDataCount,
		uiState,
		selectedChart,
		canViewSummary,
		init,
		fetchQuestions,
		fetchGroupedQuestions,
	] = useReportStore((state) => [
		state.report,
		state.saveReport,
		state.saveAsReport,
		state.setClient,
		state.activeModal,
		state.selectedQuickFilter,
		state.setSelectedQuickFilter,
		state.applySelectedQuickFilter,
		state.getFilterOptions,
		state.filterOptions,
		state.getHasChanges,
		state.pagination,
		state.setPagination,
		state.gridDataCount,
		state.uiState,
		state.selectedChart,
		state.canViewSummary,
		state.init,
		state.fetchQuestions,
		state.fetchGroupedQuestions,
	]);

	const calculateNumberOfPages = (gridDataCount: number, pageSize: number): number => {
		const numPages = Math.ceil(gridDataCount / pageSize);

		if (numPages > 0) {
			return numPages;
		}

		return 1;
	};
	// react state
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [newPageSize, setNewPageSize] = useState<number | null>(defaultReportPageSize);
	const existingReportPageSize = report?.pageSize;

	// we want pageSize to be the saved existing report page size,
	// unless the user is actively updating the page size
	// and if neither are found, default to 50
	const pageSize = report
		? newPageSize ||
			existingReportPageSize ||
			Number.parseInt(pagination.pageSize?.toString() || defaultReportPageSize.toString(), 10)
		: null;

	useEffect(() => {
		setNewPageSize(report?.pageSize || defaultReportPageSize);
	}, [report?.pageSize]);

	const pageNumber = Number.parseInt(searchParams.get("pageNumber") || pagination.pageNumber?.toString(), 10);
	const decimalPlaces = useSelector(getCompanyDecimalPlacesSetting);

	// Fetch questions and question groups for review/survey reports before grid data loads
	useEffect(() => {
		if (report?.type === "reviewReport" || report?.type === "surveyReport") {
			fetchQuestions();
			fetchGroupedQuestions();
		}
	}, [report?.type, report?.questionGroupingSessionID]);

	const client = useApolloClient();

	useEffect(() => {
		setClient(client);
	}, [client]);

	useEffect(() => {
		const initializeReport = async () => {
			if (!reportID) {
				return;
			}

			if (isInitialized.current) {
				return;
			}

			isInitialized.current = true;

			try {
				await init(reportID);
				updatePagination({ pageNumber, pageSize });
				updateQueryParams({ pageNumber, pageSize: clampPageSize(pageSize) });
			} catch (error) {
				// Handle any UI-specific error handling if needed
			}
		};

		initializeReport();
	}, [reportID]);

	useEffect(() => {
		getFilterOptions();
	}, [report?.type]);

	// toggle the open close state of the filter slideout
	const handleToggleFilterSlideout = () => {
		setIsFilterOpen(!isFilterOpen);
	};

	const handleSetFilterDataSet = () => {
		updatePagination({ pageNumber: 1, pageSize: pageSize });
		applySelectedQuickFilter();
	};

	const updateQueryParams = (update: Record<string, string | number>) => {
		mergeSearchParameters(update, { replace: true });
	};

	const updatePagination = (args: { pageNumber: number; pageSize: number | null }) => {
		const { pageNumber, pageSize } = args;
		if (pageSize) {
			setNewPageSize(pageSize);
			setPagination({
				pageNumber: pageNumber,
				pageSize: clampPageSize(pageSize),
			});

			updateQueryParams({ pageNumber, pageSize: clampPageSize(pageSize) });
		}
	};

	const changePage = (page: number) => {
		updatePagination({ pageNumber: page, pageSize: pageSize });
	};

	const handleSetSelectedQuickFilter = (quickFilter: SelectedFilter[]) => {
		setSelectedQuickFilter(quickFilter);
	};

	if (uiState === "INITIALIZING" || !pageSize || canViewSummary === undefined) {
		return <Spinner>Initializing Report</Spinner>;
	}

	if (!report) {
		return <ErrorPage code={viewReportReportPermissionDenied ? 403 : 404} />;
	}

	return (
		<div className={css.reportBuilderContainer}>
			<PageTitle title={`Reporting ${company?.name ? `- ${company.name} -` : "-"} PerformYard`} />
			{report !== null ? (
				<ReportHeader
					handleToggleFilterSlideout={handleToggleFilterSlideout}
					saveReport={saveReport}
					onSaveAs={saveAsReport}
					updatePagination={updatePagination}
					pageSize={pageSize}
					pageSizes={pageSizes}
				/>
			) : null}
			<Spacer height={1} />
			<ReportTable
				decimalPlaces={decimalPlaces}
				pageNumber={pageNumber}
				pageSize={pageSize}
				openColumnPicker={shouldOpenColumnPicker}
				updatePagination={updatePagination}
			/>
			<div>
				{filterOptions ? (
					<Filters
						selectedFilter={selectedQuickFilter}
						setSelectedFilter={handleSetSelectedQuickFilter}
						applyFilterDataSet={handleSetFilterDataSet}
						onClose={handleToggleFilterSlideout}
						filterOptions={filterOptions}
						isOpen={isFilterOpen}
					/>
				) : null}
			</div>

			{activeModal === "QUESTION_GROUP" ? <GroupQuestionModal /> : null}
			{selectedChart === ChartType.Table ? (
				<Pagination
					currentPage={pagination.pageNumber}
					pageCount={calculateNumberOfPages(gridDataCount, pageSize)}
					onClick={(page: number) => changePage(page)}
				/>
			) : null}
			{permissionToCreateReviewReport && <BeforeUnload checkForUnsavedData={getHasChanges} />}
		</div>
	);
};

export default AnalyticsReport;
